import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, CircularProgress, Typography } from '@mui/material';
import { useInvitationFlowStore } from '../stores/invitationFlowStore';
import { getInvitationInfo } from '../api/invitationApi';

/**
 * Entry point for invitation flow
 * Fetches invitation info and redirects to appropriate step
 */
const InvitationLoadingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Using shallow access for individual properties
  const setInvitationInfo = useInvitationFlowStore(
    state => state.setInvitationInfo
  );
  const setInvitationId = useInvitationFlowStore(
    state => state.setInvitationId
  );

  useEffect(() => {
    // Handle missing ID
    if (!id) {
      navigate('/invitation/error', { replace: true });
      return;
    }

    // Fetch invitation info
    const fetchInvitation = async () => {
      try {
        const response = await getInvitationInfo(id);

        // Store in Zustand
        setInvitationInfo(response);
        setInvitationId(id);

        // Navigate based on requirements
        const { requiresProfileCapture, requiresDataSharingConsent } = response;

        if (requiresProfileCapture) {
          navigate(`/invitation/${id}/profile`, { replace: true });
        } else if (requiresDataSharingConsent) {
          navigate(`/invitation/${id}/consent`, { replace: true });
        } else {
          navigate(`/invitation/${id}/processing`, { replace: true });
        }
      } catch (error) {
        console.error('Failed to load invitation:', error);
        navigate(`/invitation/${id}/error`, { replace: true });
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [id, navigate, setInvitationInfo, setInvitationId]);

  // Show loading state only when actually loading
  if (!loading) {
    return null; // Component will navigate away, no need to show anything
  }

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={3}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading invitation...
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please wait while we verify your invitation
        </Typography>
      </Box>
    </Container>
  );
};

export default InvitationLoadingPage;
